import * as S from "./styled";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { apiUpdate } from "../../../../hooks/apiUpdate";
import axiosInstance from "services/axiosInstance";
import { NumericFormat } from "react-number-format";
/**
 * hooks
 */
import { usePasswordToggle } from "../../../../hooks/usePasswordToggle";
import useLoading from '../../../../hooks/useLoading';
import apiRequest from "../../../../hooks/apiRequest";
/**
 * Components
 */
import LayoutModal from "../../../LayoutModal";
import { useEffect } from "react";
import Loading from "../../../../components/Loading";
/**
 * Icons
 */
import { Eye, EyeSlash } from "@phosphor-icons/react";

type Inputs = {
  first_name: string;
  last_name: string;
  is_admin?: number;
  is_manager?: number;
  discount: number;
  email: string;
  password?: string;
  password_confirmation?: string;
};

interface ICreate {
  isOpenModal: boolean;
  setModalClose: () => void;
  selectedUrlId: number | null;
  updateList: () => void;
}

export default function Update({
  isOpenModal,
  setModalClose,
  updateList,
  selectedUrlId,
}: ICreate) {
  const {
    register,
    handleSubmit: handleFormSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<Inputs>();

  const { inputType, icon, togglePasswordVisibility } = usePasswordToggle();
  const { isLoading, startLoading, stopLoading } = useLoading();
  // const { handleSubmit } = apiUpdate(`user/${selectedUrlId}`);

  useEffect(() => {
    const fetchData = async () => {
      startLoading();
      try {
        const response = await axiosInstance.get(`user/${selectedUrlId}`);

        const addressData = response.data;
        for (const key in addressData) {
          setValue(key as keyof Inputs, addressData[key]);
        }
      } catch (error) {
        console.log("Error: ", error);
      } finally {
        stopLoading();
      }
    };
    if (selectedUrlId !== null) {
      fetchData();
    }
  }, [selectedUrlId]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    startLoading();
    try {
      // const response = await handleSubmit(data);
      const response = await apiRequest(`user/${selectedUrlId}`, 'PATCH', data);
      const status = response.status;
      if (status === 200) {
        alert("Usuário atualizado com sucesso!");
        updateList();
        setModalClose();
      }
    } catch (error: any) {
      alert(error.message || "Ocorreu um erro ao criar o usuário.");
    } finally {
      stopLoading();
    }
  };

  if (isOpenModal) {
    return (
      <LayoutModal
        statusLoad={isLoading}
        title="Editar Usuário"
        subTitle="Atualize informações do usuário em sua base de dados."
        setModalClose={setModalClose}
        submiteModal={handleFormSubmit(onSubmit)}
        nameSubmit="Atualizar"
      >
        {isLoading ? (
          <Loading />
        ) : (
          <S.Container>
            <S.SeparateDiv>
              <S.FormInput>
                <S.Label>
                  Nome do Usuário
                  <span>*</span>
                </S.Label>
                <S.Input
                  placeholder="Informe o nome"
                  type="text"
                  {...register("first_name", { required: true })}
                />
                {errors.first_name && (
                  <S.InforErro>Campo Obrigatório</S.InforErro>
                )}
              </S.FormInput>

              <S.FormInput>
                <S.Label>
                  Sobrenome
                  <span>*</span>
                </S.Label>
                <S.Input
                  placeholder="Informe o sobrenome"
                  type="text"
                  {...register("last_name", { required: true })}
                />
                {errors.last_name && <S.InforErro>Campo Obrigatório</S.InforErro>}
              </S.FormInput>
            </S.SeparateDiv>

            <S.SeparateDiv>
              <S.FormInput>
                <S.Label>
                  E-mail <span>*</span>
                </S.Label>
                <S.Input
                  placeholder="exemplo@gmail.com"
                  type="email"
                  {...register("email", { required: true })}
                />
                {errors.email && <S.InforErro>Campo Obrigatório</S.InforErro>}
              </S.FormInput>

              <S.FormInput>
                <S.Label>
                  Desconto % <span>*</span>
                </S.Label>
                <Controller
                  name="discount"
                  control={control}
                  render={({ field: { onChange, value, ref } }) => (
                    <NumericFormat
                      className="mask"
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      fixedDecimalScale={true}
                      placeholder="0,00 %"
                      suffix=" %"
                      value={value}
                      onValueChange={(values) => {
                        const purchaseValue = values.floatValue ?? 0;
                        onChange(purchaseValue); // Atualiza o valor de compra
                      }}
                      getInputRef={ref}
                    />
                  )}
                />
              </S.FormInput>
            </S.SeparateDiv>

            <S.SeparateDiv>
              <S.FormInput>
                <S.Label>
                  Senha
                </S.Label>
                <S.ContentInput>
                  <S.InputUnstyled
                    {...register("password")}
                    type={inputType}
                    placeholder="Informe sua senha"
                  />

                  <S.ButtonOpenPassword
                    type="button"
                    onClick={togglePasswordVisibility}
                  >
                    {icon === "eye-open" ? (
                      <Eye size={20} />
                    ) : (
                      <EyeSlash size={20} />
                    )}
                  </S.ButtonOpenPassword>
                </S.ContentInput>
              </S.FormInput>

              <S.FormInput>
                <S.Label>
                  Confirmar Senha
                </S.Label>
                <S.ContentInput>
                  <S.InputUnstyled
                    {...register("password_confirmation")}
                    type={inputType}
                    placeholder="Confirme sua senha"
                  />

                  <S.ButtonOpenPassword
                    type="button"
                    onClick={togglePasswordVisibility}
                  >
                    {icon === "eye-open" ? (
                      <Eye size={20} />
                    ) : (
                      <EyeSlash size={20} />
                    )}
                  </S.ButtonOpenPassword>
                </S.ContentInput>
              </S.FormInput>
            </S.SeparateDiv>

            <S.FormInput>
              <S.Label>
                Tipo de Usuário
                <span>*</span>
              </S.Label>

              <S.ContentCheckbox>
                <S.CheckBox type="checkbox" id="is_admin"  {...register('is_admin', {
                  setValueAs: (value) => value ? 1 : 0
                })} />
                <S.Label htmlFor="is_admin">
                  Administrador
                </S.Label>
              </S.ContentCheckbox>

              <S.ContentCheckbox>
                <S.CheckBox type="checkbox" id="is_manager" {...register('is_manager', {
                  setValueAs: (value) => value ? 1 : 0
                })} />
                <S.Label htmlFor="is_manager">
                  Gerente
                </S.Label>
              </S.ContentCheckbox>
              {errors.is_admin && <S.InforErro>Campo Obrigatório</S.InforErro>}
            </S.FormInput>
          </S.Container>
        )}
      </LayoutModal>
    );
  }
  return null;
}
